import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Globe, ChevronDown, ChevronRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [servicesDropdown, setServicesDropdown] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const location = useLocation()
  const { language, setLanguage, availableLanguages } = useLanguage()
  const t = useTranslation(language)

  // Effet pour détecter le scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Liens principaux
 const mainNavLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.services'), path: '/services', hasDropdown: true },
    { name: t('nav.aiSolutions'), path: '/ai-solutions' },
    { name: t('nav.training'), path: '/training' },
    { name: t('nav.recruitment'), path: '/recruitment' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' }
]

  // Sous-menu Services
  const servicesLinks = [
    { name: t('nav.services'), path: '/services'},
    { name: t('nav.conseil'), path: '/conseil' },
    { name: t('nav.accompagnement'), path: '/accompagnement' },
    { name: t('nav.partnerships'), path: '/partnerships' }
  ]

  const isActive = (path) => location.pathname === path
  const isServicesActive = () => 
    servicesLinks.some(link => isActive(link.path)) || isActive('/services')

  // Fermer le menu mobile quand on change de page
  const handleMobileLinkClick = () => {
    setIsOpen(false)
    setMobileServicesOpen(false)
  }

  // Toggle du menu services mobile
  const toggleMobileServices = () => {
    setMobileServicesOpen(!mobileServicesOpen)
  }

  return (
    <nav className={`fixed w-full backdrop-blur-sm z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 shadow-lg border-b border-gray-200' 
        : 'bg-white/10 shadow-md'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="h-18 flex items-center justify-center transform group-hover:scale-105 transition-transform duration-200">
              <img 
                src="/images/logo.webp" 
                alt="Texaconsult" 
                className="h-20 w-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {mainNavLinks.map((link) => (
              <div key={link.path} className="relative">
                {link.hasDropdown ? (
                  // Menu Services avec dropdown
                  <div 
                    className="relative"
                    onMouseEnter={() => setServicesDropdown(true)}
                    onMouseLeave={() => setServicesDropdown(false)}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                        isServicesActive()
                          ? 'text-blue-600 bg-blue-50 border-b-2 border-blue-600'
                          : isScrolled 
                            ? 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                            : 'text-white hover:text-blue-200 hover:bg-white/20'
                      }`}
                    >
                      {link.name}
                      <ChevronDown size={16} className="ml-1" />
                    </Link>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {servicesDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                        >
                          {servicesLinks.map((sublink) => (
                            <Link
                              key={sublink.path}
                              to={sublink.path}
                              className={`block px-4 py-2 text-sm transition-all duration-200 ${
                                isActive(sublink.path)
                                  ? 'text-blue-600 bg-blue-50 font-semibold'
                                  : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                              }`}
                              onClick={() => setServicesDropdown(false)}
                            >
                              {sublink.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  // Lien normal
                  <Link
                    to={link.path}
                    className={`px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                      isActive(link.path)
                        ? 'text-blue-600 bg-blue-50 border-b-2 border-blue-600'
                        : isScrolled 
                          ? 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                          : 'text-white hover:text-blue-200 hover:bg-white/20'
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            
            {/* Language Selector */}
            <div className="ml-4">
              <div className="flex items-center space-x-2">
                <Globe size={16} className={isScrolled ? "text-gray-600" : "text-white"} />
                <select 
                  value={language} 
                  onChange={(e) => setLanguage(e.target.value)}
                  className={`px-3 py-2 rounded-md border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer ${
                    isScrolled
                      ? 'border-gray-300 bg-white text-gray-700 focus:border-blue-500'
                      : 'border-white/30 bg-white/10 text-white focus:border-white'
                  }`}
                >
                  {availableLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code} className="text-gray-700">
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Language Selector Mobile */}
            <div className="flex items-center space-x-1">
              <Globe size={16} className={isScrolled ? "text-gray-600" : "text-white"} />
              <select 
                value={language} 
                onChange={(e) => setLanguage(e.target.value)}
                className={`px-2 py-1 rounded-md border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer ${
                  isScrolled
                    ? 'border-gray-300 bg-white text-gray-700 focus:border-blue-500'
                    : 'border-white/30 bg-white/10 text-white focus:border-white'
                }`}
              >
                {availableLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code} className="text-gray-700">
                    {lang.flag}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-md transition-colors duration-200 ${
                isScrolled
                  ? 'text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white/20'
              }`}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-3 space-y-1">
              {mainNavLinks.map((link) => (
                <div key={link.path}>
                  {link.hasDropdown ? (
                    // Menu déroulant mobile pour Services (avec toggle)
                    <div>
                      <button
                        onClick={toggleMobileServices}
                        className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                          isServicesActive()
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                        }`}
                      >
                        <span>{link.name}</span>
                        <ChevronRight 
                          size={16} 
                          className={`transition-transform duration-200 ${
                            mobileServicesOpen ? 'rotate-90' : ''
                          }`} 
                        />
                      </button>
                      
                      {/* Sous-menu mobile avec animation */}
                      <AnimatePresence>
                        {mobileServicesOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="ml-4 space-y-1 overflow-hidden"
                          >
                            {servicesLinks.map((sublink) => (
                              <Link
                                key={sublink.path}
                                to={sublink.path}
                                onClick={handleMobileLinkClick}
                                className={`block px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                                  isActive(sublink.path)
                                    ? 'text-blue-600 bg-blue-50 border-r-2 border-blue-600'
                                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                                }`}
                              >
                                {sublink.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    // Lien normal mobile
                    <Link
                      to={link.path}
                      onClick={handleMobileLinkClick}
                      className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                        isActive(link.path)
                          ? 'text-blue-600 bg-blue-50 border-r-4 border-blue-600'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Language Selector Mobile */}
              <div className="pt-4 border-t border-gray-200">
                <p className="px-3 py-2 text-sm font-medium text-gray-500">
                  {'Language'}
                </p>
                <select 
                  value={language} 
                  onChange={(e) => {
                    setLanguage(e.target.value)
                    handleMobileLinkClick()
                  }}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-base font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer"
                >
                  {availableLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Navbar